/**
 * VAVCI · Carrito de la página de producto (PDP).
 *
 * El form #vc-product-form era un {% form 'product' %} plano: al pulsar "Añadir"
 * hacía POST normal y recargaba la página. Aquí lo convertimos en alta AJAX que:
 *   1. Añade la variante seleccionada (+ cantidad) a la cesta.
 *   2. Emite CartLinesUpdateEvent (@shopify/events): cart-icon.js sube el badge y
 *      cart-items-component refresca el contenido del drawer (morph de la sección).
 *   3. ABRE el cart-drawer (modal) — el mismo drawer del tema, con su animación.
 *
 * El botón de pago dinámico (Apple/Google/Shop Pay) lo pinta Liquid con
 * `{{ form | payment_button }}` (nativo) y gestiona su propio flujo express; este
 * script NO lo intercepta (solo intercepta el submit del form de añadir).
 *
 * Nota: NO usamos el auto-open global del tema porque abriría también el drawer en
 * las tarjetas del Home (allí se quiere vuelo + badge, sin drawer). Por eso aquí el
 * drawer se abre de forma explícita.
 *
 * Mejora progresiva: sin JS, el form hace POST normal y la cesta sigue funcionando.
 * CSP-safe. Convive con el script inline de la sección (galería, variantes, qty,
 * sticky), que no se toca.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { giftLineToAdd } from './vavci-gift.js';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const FORM_ID = 'vc-product-form';
let inFlight = false;

/** Abre el cart-drawer de Horizon. Devuelve true si pudo abrirlo. */
function openCartDrawer() {
  // 1) El trigger del header abre el drawer vía el binding nativo del tema (lo más fiable).
  const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
  if (trigger) { trigger.click(); return true; }
  // 2) Llamar open() directamente sobre el custom element.
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function') { drawer.open(); return true; }
  return false;
}

function setBusy(busy) {
  document.querySelectorAll('[data-vc-add], [data-vc-sticky-add]').forEach((b) => {
    if (busy) b.setAttribute('aria-busy', 'true');
    else b.removeAttribute('aria-busy');
  });
}

/** Avisa a los módulos de la ficha (reglas B2B, etc.) por evento sobre [data-vc-pdp]. */
function emitPdp(name, detail) {
  const pdp = document.querySelector('[data-vc-pdp]');
  if (pdp) pdp.dispatchEvent(new CustomEvent(name, { detail }));
}

// Interceptamos el CLICK del botón (no el submit del form): el botón es type="button",
// así no hay submit nativo ni recarga, y el SDK del pago dinámico (que engancha el
// submit del form) no puede interferir. El form se conserva solo para el pago express.
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-vc-add], [data-vc-sticky-add]');
  if (!btn || btn.hasAttribute('disabled') || btn.getAttribute('aria-busy') === 'true') return;
  const form = document.getElementById(FORM_ID);
  if (!form) return;
  event.preventDefault();
  addToCart(form);
});

async function addToCart(form) {
  if (inFlight) return;
  inFlight = true;
  setBusy(true);

  const formData = new FormData(form);

  // Suscripción (WU4): el plan viaja en el input oculto selling_plan del form
  // (initPurchase lo mantiene; en compra única está `disabled` y FormData ya lo
  // excluye). Aquí solo limpieza: los radios de modo son UI pura y un
  // selling_plan vacío no debe llegar al POST.
  formData.delete('vc-mode');
  if (!formData.get('selling_plan')) formData.delete('selling_plan');

  // Pide el render de las secciones del carrito para refrescar el drawer sin recargar.
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });
  if (sectionIds.size) formData.set('sections', [...sectionIds].join(','));

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  // Evento optimista: cart-icon.js sube el badge; cart-items-component refresca el drawer.
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${ROOT}cart/add`, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();

    if (data.status) { // error (p. ej. sin stock / límite / regla de cantidad B2B)
      deferred.resolve({ detail: { didError: true, source: 'vavci-pdp-cart' } });
      const message = String(data.description || data.message || data.status);
      console.warn('[vavci-pdp-cart] add to cart error:', message);
      // Antes el error moría en consola y el cliente pulsaba «Añadir» sin ver nada.
      // El aviso existe siempre en buy_buttons; texto plano del servidor → textContent.
      const msgEl = document.querySelector('[data-vc-qty-msg]');
      if (msgEl) msgEl.textContent = message;
      emitPdp('vavci:cart-error', { message });
      return;
    }

    let cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    let sections = data.sections;

    // Regalo promocional (bloque `gift` de la PDP): si el DOM lo anuncia y aún no
    // está en la cesta, se añade en un segundo POST con las MISMAS sections para
    // que el HTML del drawer ya lo incluya. Best-effort a conciencia: el descuento
    // BXGY lo aplica Shopify en el checkout, así que un fallo aquí solo se avisa
    // por consola y el alta principal sigue su curso intacta.
    try {
      const giftBox = document.querySelector('[data-vc-gift]');
      const giftLine = giftBox ? giftLineToAdd({ cart, giftVariantId: giftBox.getAttribute('data-variant-id') }) : null;
      if (giftLine) {
        const giftBody = { items: [giftLine] };
        if (sectionIds.size) giftBody.sections = [...sectionIds].join(',');
        const giftResponse = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(giftBody),
        });
        const giftData = await giftResponse.json();
        if (giftData.status) {
          console.warn('[vavci-pdp-cart] no se pudo añadir el regalo:', giftData.description || giftData.message || giftData.status);
        } else {
          // Carrito FINAL (con el regalo) y sections más recientes: es lo que debe
          // ver el drawer y lo que debe contar la barra de envío gratis.
          cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
          if (giftData.sections) sections = giftData.sections;
        }
      }
    } catch (giftError) {
      console.warn('[vavci-pdp-cart] regalo omitido por error:', giftError);
    }

    // Refresca la barra de envío gratis de la PDP con el total ya actualizado.
    // Reutiliza este mismo `cart` → sin peticiones de red adicionales.
    if (typeof window.vcShipBar === 'function') window.vcShipBar(cart.total_price);
    // Reglas B2B: vavci-pdp-rules.js recalcula lo que ya hay de cada variante con
    // este MISMO carrito (Shopify valida mínimo/incremento/máximo contra el total
    // de la línea). Sin peticiones de red nuevas.
    emitPdp('vavci:cart', { cart });

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'vavci-pdp-cart', itemCount: quantity, sections, didError: false },
    });

    if (!openCartDrawer()) window.location.href = `${ROOT}cart`;
  } catch (error) {
    if (typeof deferred.reject === 'function') deferred.reject(error);
    console.warn('[vavci-pdp-cart] add to cart failed:', error);
  } finally {
    inFlight = false;
    setBusy(false);
  }
}
